// The cover-sheet config documents are hand-authored JSON, so these shapes are
// hand-written too: every field is optional and nullable because the documents
// omit whatever a card does not use.

export enum CommissionType {
  AMOUNT = 'AMOUNT',
  PERCENTAGE = 'PERCENTAGE',
}

export enum CoverSheetIconType {
  dollar = 'dollar',
  percentage = 'percentage',
  autoAwesome = 'autoAwesome',
  uploadFile = 'uploadFile',
}

type ActionLabel = {
  primary?: string | null;
  secondary?: string | null;
};

type MapActionProp = {
  from: string;
  label?: string | null;
  to: string;
};

type DialogAction = {
  ariaLabel: string;
  label: string;
  type: string;
};

export type Dialog = {
  actions?: DialogAction[] | null;
  content: string;
  title: string;
  type: string;
};

type MlsAutofillFieldsAction = {
  number: string;
  vendorId: string;
};

export type Action = {
  constraints?: string[] | null;
  dialog?: Dialog[] | null;
  label?: ActionLabel | null;
  mlsAutofillFields?: MlsAutofillFieldsAction | null;
  name?: string | null;
  props?: MapActionProp[] | null;
  provider?: string | null;
};

type ShowWhen = {
  fieldName: string;
  hasValue: string;
};

export type CollapsibleDisplayField = {
  name: string;
  type?: string | null;
};

type CustomField = {
  label: string;
  name: string;
  placeholderText: string;
};

export type LabelWhen = {
  fieldName: string;
  hasValue: string;
  label: string;
};

export type Layout = {
  name: string;
  size: number;
  mobileSize?: number | null;
  tabletSize?: number | null;
  /**
   * Overrides `size`, which otherwise holds from `lg` up. Use it to keep a dense
   * row from packing in before there is room for it.
   */
  xl?: number | null;
  /**
   * Lets the field's label run past its own column instead of being clipped.
   * Only safe when whatever shares the row is shorter than the label's line —
   * a toggle sitting beside an input, say.
   */
  labelSpansRow?: boolean | null;
};

type Option = {
  disabled?: boolean | null;
  icon?: string | null;
  label: string;
  labelWhen?: LabelWhen | null;
  showWhen?: ShowWhen | null;
  value: string;
};

export type Validation = {
  appliesToIndex?: number[] | null;
  message?: string | null;
  type: string;
  value?: string | null;
};

export type Field = {
  name: string;
  entity?: string | null;
  entityType?: string | null;
  type: string;
  label: string;
  value?: string | null;
  placeholderText?: string | null;
  tooltip?: string | null;
  variant?: string | null;
  disabled?: boolean | null;
  disabledForIndex?: number | null;
  visibleForIndex?: number | null;
  maxLength?: number | null;
  labelWhen?: LabelWhen | null;
  validation?: Validation[] | null;
  options?: Option[] | null;
  customField?: CustomField | null;
  action?: Action | null;
  showWhen?: ShowWhen | null;
};

export type Card = {
  id: string;
  entity?: string | null;
  entityType?: string | null;
  innerTitle?: string | null;
  outerTitle?: string | null;
  width?: string | null;
  legend?: string | null;
  fields: Field[];
  layout?: (Layout[] | null)[] | null;
  action?: Action | null;
};

export type Section = {
  id: string;
  header: string;
  sidebarText: string;
  cards: Card[];
};

export type CoverSheetConfig = {
  locale: string;
  version?: string | null;
  sections: Section[];
};

// Builder helper types
export type CoverSheetConfigCard = Omit<Card, 'fields'> & {
  fields: Record<string, Field | null>;
  order?: number;
};

export type CoverSheetConfigSection = Omit<Section, 'cards'> & {
  cards: Record<string, CoverSheetConfigCard | null>;
};

type CoverSheetConfigObject = Omit<CoverSheetConfig, 'sections'> & {
  sections: Record<string, CoverSheetConfigSection>;
};

export type DefaultCoverSheetConfig = Omit<CoverSheetConfigObject, 'locale'>;

/**
 * Transforms a specific primitive value into its wider primitive type.
 * If the value is not a primitive the original type is returned.
 */
type ToPrimitive<T> = T extends string
  ? string
  : T extends number
    ? number
    : T extends boolean
      ? boolean
      : T;

/**
 * Broadens a specific type like { id: 'card', fields: {...} }
 * into a generic type like Card.
 */
type Broaden<T> = T extends Field
  ? Field
  : T extends Card | CoverSheetConfigCard
    ? CoverSheetConfigCard
    : T extends Section | CoverSheetConfigSection
      ? CoverSheetConfigSection
      : T extends DefaultCoverSheetConfig
        ? CoverSheetConfigObject
        : T extends Record<string, Field>
          ? Record<string, Field>
          : T extends Record<string, CoverSheetConfigCard>
            ? Record<string, CoverSheetConfigCard>
            : T extends Record<string, CoverSheetConfigSection>
              ? Record<string, CoverSheetConfigSection>
              : ToPrimitive<T>;

// Types that can be replaced with `null` to be removed
type NullableTypes = Field | CoverSheetConfigCard;

// Types that can be extended with new keys in the override object
type ExtendableTypes = Field | CoverSheetConfigCard | CoverSheetConfigSection;

/**
 * Describes a coversheet overrides object that can be processed by applyOverrides.
 *
 * Supports:
 * - Replacing already defined properties and subproperties of T with a value of the
 * same type. If the property has a type specified in `NullableTypes` it can also be
 * replaced with `null` to be removed.
 * - Specifying a mapper function to transform an existing defined property. Note that
 * nested mapper functions are not supported.
 * - Specifing new keys on extendable types, e.g. if a `Field` doesn't have `validation`
 * in the default config it can be added in the override.
 */
export type CoverSheetOverrides<T> =
  | Broaden<T>
  | (T extends (infer U)[]
      ? CoverSheetOverrides<U>[]
      : T extends object
        ? {
            [K in keyof T]?:
              | ((defaultValue: T[K]) => Broaden<T[K]>) // Allow mapping the existing property value
              // Allow defining new keys on extendable object types
              | (Broaden<T[K]> extends ExtendableTypes
                  ? Omit<Broaden<T[K]>, keyof T[K]>
                  : never)
              // Allow nulling nullable types
              | (Broaden<T[K]> extends NullableTypes ? null : never)
              // Allow replacing properties with any value that has same widened type
              | (K extends keyof Broaden<T> ? Broaden<T>[K] : never)
              // Recurse into subproperties
              | CoverSheetOverrides<T[K]>;
          }
        : T);

export type LocaleOverrides<T extends DefaultCoverSheetConfig> =
  CoverSheetOverrides<T> & { locale: string };
